import { isFinite } from 'lodash';
import { round, debounce } from 'lodash';
import { v4 as uuid } from 'uuid';
import { trackEvent } from '../../search/helpers/google_analytics_helpers';

import { insightsPropertyTypeMappings, insightsPropertySubtypeMappings } from '../../search/components/filters/constants';

export const UPDATE_CRITERIA = 'UPDATE_CRITERIA';
export const SET_CRITERIA = 'SET_CRITERIA';
export const TOGGLE_ASSET_CLASS = 'TOGGLE_ASSET_CLASS';
export const CLEAR_CRITERIA = 'CLEAR_CRITERIA';

const pendoTrackFilterEvent = debounce(function (eventName, filterKey, offMarketSearch) {
  if (window.pendo) {
    window.pendo.track(
      `${offMarketSearch ? 'All Properties' : 'Exclusive Listings'} - ${eventName}`,
      { field: filterKey }
    );
  }
}, 300);

const pendoTrackPropertySubTypeEvent = debounce(function (eventName, typeId) {
  if (window.pendo) {
    const propertySubType = insightsPropertySubtypeMappings[typeId];
    const propertyType = insightsPropertyTypeMappings[typeId] ? insightsPropertyTypeMappings[typeId] : insightsPropertyTypeMappings[Math.floor(typeId / 100)];

    window.pendo.track(
      `${eventName}`,
      { propertyType: propertyType, propertySubType: propertySubType }
    );
  }
}, 300);

// Cleans up a criteria location name
function sanitizeLocationName(name) {
  if (!name) return name;

  return name.replace(', USA', '');
}

export function clearBounds() {
  return {
    type: UPDATE_CRITERIA,
    payload: {
      locationsAttributes: undefined
    }
  };
}

export function clearFilters() {
  return (dispatch) => {
    dispatch({ type: CLEAR_CRITERIA });
  };
}

export function removeLocation(id) {
  return (dispatch, getState) => {
    const { criteria } = getState();

    const locationsAttributes = [...criteria.locationsAttributes].filter(loc => (loc.id != id));

    dispatch({
      type: UPDATE_CRITERIA,
      payload: { locationsAttributes }
    });
  };
}

function getPolygon(latLngArray = []) {
  // transform input to array of [lng, lat] to send to server
  const coordinates = latLngArray.map(({ lat, lng }) => [lng, lat]);

  // Valid polygons need to start and end on the same point
  if (coordinates[0] !== coordinates[latLngArray.length - 1]) {
    coordinates.push([...coordinates[0]]);
  }

  // polygon geojson is an array of polygons, so we need to intentionally wrap in array
  return [coordinates];
}

export function addBound(latLngArray, options: { name?: string } = {}) {
  const polygon = getPolygon(latLngArray);

  return (dispatch, getState) => {
    const { criteria } = getState();

    // Case 1: We add a new location bound. We only clear all existing map drawing polygons.
    // Case 2: We add a new map drawing. We clear all existing polygons.
    const locationsAttributes = (criteria.locationsAttributes && options.name) ?
      criteria.locationsAttributes.filter(attributes => attributes.name)
      : [];

    // exclude locations that have already been added, or are just the view of the map
    if (
      locationsAttributes.some(loc => options.name && sanitizeLocationName(options.name) === loc.name)
    ) return;

    trackEvent('connect_search_bounds_added', { name: sanitizeLocationName(options.name) });
    dispatch({
      type: UPDATE_CRITERIA,
      payload: {
        locationsAttributes: [...locationsAttributes, {
          name: sanitizeLocationName(options.name),
          polygonSearch: true,
          id: uuid(),
          polygonGeojson: {
            type: 'Polygon',
            coordinates: polygon
          }
        }]
      }
    });
  };
}

export function addGeoJsons(geoJsonList) {
  return (dispatch, getState) => {
    const { criteria } = getState();

    const updatedLocations = [...criteria.locationsAttributes?.filter(attr => attr.name) || []];

    for (const { geoJson, address } of geoJsonList) {
      const sanitizedName = sanitizeLocationName(address);

      if (updatedLocations.some(attr => attr.name === sanitizedName)) continue;

      trackEvent('connect_search_location_selected', { name: sanitizedName });

      updatedLocations.push({
        name: sanitizedName,
        polygonSearch: true,
        id: uuid(),
        polygonGeojson: geoJson,
      });
    }

    dispatch({
      type: UPDATE_CRITERIA,
      payload: {
        mapBounds: undefined,
        locationsAttributes: updatedLocations,
      },
    });
  };
}

export function addGeoJson(polygonGeojson, options: { name?: string } = {}) {
  if (!polygonGeojson) return;

  return (dispatch, getState) => {
    const { criteria } = getState();
    const sanitizedName = sanitizeLocationName(options.name);

    const locationsAttributes = [
      ...criteria.locationsAttributes?.filter(attr => attr.name && attr.name !== sanitizedName) || []
    ];

    trackEvent('connect_search_location_selected', { name: sanitizedName });

    dispatch({
      type: UPDATE_CRITERIA,
      payload: {
        mapBounds: undefined,
        locationsAttributes: [...locationsAttributes, {
          name: sanitizedName,
          polygonSearch: true,
          id: uuid(),
          polygonGeojson
        }]
      }
    });
  };
}

// bounds is array of [{ lat:, lng:}]
export function replaceMapBounds(latLngArray) {
  return (dispatch) => {
    const latitudes = latLngArray.map(({ lat }) => lat);
    const longitudes = latLngArray.map(({ lng }) => lng);
    const north = Math.max(...latitudes);
    const south = Math.min(...latitudes);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    dispatch({
      type: UPDATE_CRITERIA,
      payload: { mapBounds: { north, east, south, west } }
    });
  };
}

export function toggleAssetClassId(id, toggle) {
  if (toggle) pendoTrackPropertySubTypeEvent('Property Type Filter Connect', id);
  return (dispatch) => {
    dispatch({ type: TOGGLE_ASSET_CLASS, payload: { id, toggle } });
  };
}

export function setFilterAttribute(key, val, options?) {
  return (dispatch, getState) => {
    if (options && options.int) {
      val = parseInt(val.replace(/\D/g, ''));
      if (!isFinite(val)) val = undefined;
    }

    if (options && options.float) {
      val = round(parseFloat(val.replace(/[^\d.-]+/g, '')), 2);
      if (parseFloat(val) === 0) val = '0'; // prevents user from entering redundant zeroes
      if (!isFinite(val) && parseFloat(val) !== 0) val = undefined;
    }

    if (options && options.percent && val) {
      val = round(parseFloat(val.replace(/[^\d.-]+/g, '')), 2);
      if (val > 100 || val < 0) return { type: 'noop' };
    }

    if (options && options.array && val !== undefined && val !== null) {
      const { criteria } = getState();
      const existingArr = criteria[key] || [];
      if (existingArr.includes(val)) {
        val = existingArr.filter(currentVal => currentVal !== val);
      } else {
        val = [...existingArr, val];
      }
    }

    // need to check for Key, map bounds updaters like region will have an undefined key
    if (key) pendoTrackFilterEvent('Criteria Updated', key, getState().ui.offMarketSearch);
    dispatch({
      type: UPDATE_CRITERIA,
      payload: {
        [key]: val
      }
    });
  };
}

export function setAssociatedSearchFilters(associatedSearchFilters) {
  return {
    type: SET_CRITERIA,
    payload: {
      ...associatedSearchFilters
    }
  };
}
